import './StoryViewer.sass';

import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Header, Placeholder, ScrollViewVirtualized } from '$uikit';
import { Loader, UserCell } from '$shared/components';
import { StoryViewsProps } from '$core/StoryViews/StoryViews.interface';
import { storiesActions, storiesSelector } from '$store/stories';
import { useDate, useTranslator } from '$hooks';
import { StoriesView } from '$store/models';
import { IcViews24 } from '$assets';

const ROW_HEIGHT = 68;

export const StoryViewUser: FC<StoriesView> = ({ userId, createdAt }) => {
  const caption = useDate(createdAt);

  return <UserCell caption={caption} objectId={userId} />;
};

export const StoryViews: FC<StoryViewsProps> = ({ storyId }) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { views, viewsTotal, isLoadingViews, viewsLoadingFailed, viewsNextFrom } =
    useSelector(storiesSelector);

  useEffect(() => {
    dispatch(storiesActions.loadViews(storyId));
    return () => {
      dispatch(storiesActions.clearViews());
    };
  }, [storyId, dispatch]);

  const handleLoadMore = useCallback(() => {
    dispatch(storiesActions.loadViews(storyId));
  }, [storyId]);

  return (
    <>
      <Header
        showBackButton
        rightContent={
          viewsTotal > 0 ? (
            <div className="StoryViews__views">
              <IcViews24 />
              <span>{viewsTotal}</span>
            </div>
          ) : undefined
        }
      >
        {t('story_views_title')}
      </Header>
      {isLoadingViews ? (
        <Loader isWrapped />
      ) : viewsLoadingFailed ? (
        <Placeholder title={t('failed_title')}>{t('failed_caption')}</Placeholder>
      ) : !views.length ? (
        <Placeholder title={t('story_views_empty_title')}>
          {t('story_views_empty_description')}
        </Placeholder>
      ) : (
        <ScrollViewVirtualized
          rowCount={views.length}
          rowHeight={ROW_HEIGHT}
          renderRow={(index) => <StoryViewUser {...views[index]} />}
          canLoadMore={!isLoadingViews && !!viewsNextFrom}
          onLoadMore={handleLoadMore}
        />
      )}
    </>
  );
};
